// src/pages/BuildingCorpsPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import * as XLSX from 'xlsx';

import { Snackbar, Alert, Badge, Tooltip, CircularProgress, Drawer } from '@mui/material';
import { showSoftSuccess } from '../utils/softSuccess';

import CorpsList from '../components/BuildingCorps/CorpsList';
import AddCorpsDialog from '../components/BuildingCorps/AddCorpsDialog';
import ComplexesToolbar from '../components/Complexes/ComplexesToolbar';
import ManualSubmissionPanel from '../components/ManualSubmission/ManualSubmissionPanel';
import HeatManagementPanel from '../components/Heat/HeatManagementPanel';
import NotificationSchedulesDialog from '../components/Complexes/NotificationSchedulesDialog';
import BuildingChannelMap from '../components/CommunicationEquipment/BuildingChannelMap';
import BuildingDevicesSection from '../components/Devices/BuildingDevicesSection';
import OrphanDevicesPanel from '../components/Devices/OrphanDevicesPanel';

import { $corpsByBuilding, fetchCorpsByBuildingFx } from '../store/buildingCorps';
import { $canDeleteComplex, $isSettlementTenant, $tenantDataStartDate } from '../store/auth';
import { $complexes } from '../store/complexes';
import { useBuildingMeta } from '../hooks/useBuildingMeta';
import { reportsApi } from '../api/reports';
import { buildFileName, downloadBlobAsFile } from '../utils/download';
import { buildingsApi } from '../api/buildings';
import { buildingCorpsApi } from '../api/buildingCorps';
import { notificationSchedulesApi } from '../api/notificationSchedules';
import { manualApi } from '../api/manual';
import { apartmentsApi } from '../api/apartments';
import { isSettlementComplex } from '../utils/complexKind';

const currentMonth = () => new Date().toISOString().slice(0, 7);

const previousMonth = (month) => {
  const match = /^(\d{4})-(\d{2})$/.exec(String(month || ''));
  if (!match) return currentMonth();
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 2, 1));
  return date.toISOString().slice(0, 7);
};

const toPercent = (value) => Math.round(Math.max(0, Math.min(1, Number(value || 0))) * 100);

const formatApartmentDate = (value, withTime = false) => {
  if (!value) return 'Не указано';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Не указано';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
};

const formatReadingValue = (value) => {
  if (value === null || value === undefined || value === '') return 'Нет показаний';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 'Нет показаний';
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 4 }).format(numeric);
};

const getReadingUnit = (systemType) => {
  const normalized = String(systemType || '').toLowerCase();
  if (normalized.includes('хвс') || normalized.includes('гвс') || normalized.includes('вод')) return 'м³';
  if (normalized.includes('элект')) return 'кВт·ч';
  if (normalized.includes('теплосч') || normalized.includes('отоп')) return 'Гкал';
  return '';
};

const naturalSortKey = (value) => {
  if (value === null || value === undefined || value === '') return [[2, '']];
  return String(value)
    .trim()
    .toLowerCase()
    .split(/(\d+)/)
    .filter(Boolean)
    .map((part) => (/^\d+$/.test(part) ? [0, Number(part)] : [1, part]));
};

const compareNatural = (a, b) => {
  const left = naturalSortKey(a);
  const right = naturalSortKey(b);
  const length = Math.max(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    const x = left[index] || [2, ''];
    const y = right[index] || [2, ''];
    if (x[0] !== y[0]) return x[0] - y[0];
    if (x[1] < y[1]) return -1;
    if (x[1] > y[1]) return 1;
  }
  return 0;
};

const floorTone = (progress, totalDevices) => {
  if (!totalDevices) return 'empty';
  if (progress >= 1) return 'complete';
  if (progress > 0) return 'partial';
  return 'none';
};

const emptyStatusCounts = () => ({
  complete: 0,
  partial: 0,
  none: 0,
  no_devices: 0,
});

const countApartmentStatuses = (apartments) =>
  apartments.reduce((acc, apartment) => {
    const status = apartment.status || 'none';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, emptyStatusCounts());

const mergeStatusCounts = (target, source) => {
  Object.entries(source || {}).forEach(([key, value]) => {
    target[key] = (target[key] || 0) + Number(value || 0);
  });
  return target;
};

const getAttentionCount = (counts) => (counts?.partial || 0) + (counts?.none || 0);

const isApartmentVisible = (apartment, filter, layer = 'readings') => {
  if (filter === 'all') return true;
  if (layer === 'devices') {
    const totalDevices = Number(apartment.total_devices || 0);
    if (filter === 'empty') return totalDevices === 0;
    if (filter === 'single') return totalDevices === 1;
    if (filter === 'multiple') return totalDevices >= 2;
    return true;
  }
  if (filter === 'attention') return apartment.status === 'partial' || apartment.status === 'none';
  if (filter === 'complete') return apartment.status === 'complete';
  if (filter === 'empty') return apartment.status === 'no_devices';
  return true;
};

const getDeviceTone = (totalDevices) => {
  const count = Number(totalDevices || 0);
  if (count === 0) return 'device-empty';
  if (count === 1) return 'device-single';
  if (count === 2) return 'device-double';
  return 'device-multiple';
};

const getFloorDeviceTone = (totalDevices, totalApartments) => {
  if (!totalDevices || !totalApartments) return 'device-empty';
  const average = Number(totalDevices) / Number(totalApartments);
  if (average <= 1) return 'device-single';
  if (average <= 2) return 'device-double';
  return 'device-multiple';
};

const apartmentStatusLabel = (status) => ({
  complete: 'Показания собраны',
  partial: 'Собраны частично',
  none: 'Нет показаний',
  no_devices: 'Нет приборов',
}[status] || 'Нет данных');

const escapeHtml = (value) => String(value ?? '—')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const safeExportName = (value, fallback = 'дом') => {
  const normalized = String(value || fallback)
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim();
  return normalized || fallback;
};

const formatPeriodDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('ru-RU');
};

const buildMasterRows = (data, filter, layer) => (data?.items || [])
  .filter((apartment) => isApartmentVisible(apartment, filter, layer))
  .sort((a, b) => (
    compareNatural(a.corp_name, b.corp_name)
    || compareNatural(a.section_number, b.section_number)
    || compareNatural(a.floor_number, b.floor_number)
    || compareNatural(a.apartment_number, b.apartment_number)
  ))
  .map((apartment) => ({
    'Корпус': apartment.corp_name || '—',
    'Подъезд': apartment.section_number || '—',
    'Этаж': apartment.floor_number || '—',
    'Квартира': apartment.apartment_number,
    'Лицевой счёт': apartment.account_number || '—',
    'Типы приборов': (apartment.system_types || []).join(', ') || '—',
    'Приборов': apartment.total_devices,
    'С показаниями': apartment.submitted_devices,
    'Без показаний': apartment.missing_devices,
    'Состояние': apartmentStatusLabel(apartment.status),
    'Проверить приборы': (apartment.missing_device_labels || []).join('; ') || '—',
    'Отметка мастера': '',
  }));

const getFloorKey = (floorNumber) => String(floorNumber ?? 'none');

const compareFloorsDescending = (a, b) => {
  if (a.floor_number == null && b.floor_number == null) return 0;
  if (a.floor_number == null) return 1;
  if (b.floor_number == null) return -1;
  return compareNatural(b.floor_number, a.floor_number);
};

function buildFloorMnemonic(progress, buildingMeta) {
  const items = Array.isArray(progress?.items) ? progress.items : [];
  const corpsMap = new Map();
  const statusCounts = countApartmentStatuses(items);
  const deviceCounts = items.reduce((acc, item) => {
    const total = Number(item.total_devices || 0);
    if (total === 0) acc.empty += 1;
    else if (total === 1) acc.single += 1;
    else acc.multiple += 1;
    return acc;
  }, { empty: 0, single: 0, multiple: 0 });
  let totalDevices = 0;
  let submittedDevices = 0;
  let hasFloorData = false;
  let hasEntranceData = false;

  items.forEach((item) => {
    const corpKey = String(item.corp_id || item.corp_name || 'none');
    const entranceKey = String(item.section_number || 'none');
    const floorKey = String(item.floor_number || 'none');
    if (item.floor_number) hasFloorData = true;
    if (item.section_number) hasEntranceData = true;
    if (!corpsMap.has(corpKey)) {
      corpsMap.set(corpKey, {
        corp_id: item.corp_id,
        corp_name: item.corp_name || 'Корпус',
        entrances: new Map(),
      });
    }
    const corp = corpsMap.get(corpKey);
    if (!corp.entrances.has(entranceKey)) {
      corp.entrances.set(entranceKey, {
        section_number: item.section_number,
        floors: new Map(),
        total_devices: 0,
        submitted_devices: 0,
        total_apartments: 0,
        status_counts: emptyStatusCounts(),
      });
    }
    const entrance = corp.entrances.get(entranceKey);
    if (!entrance.floors.has(floorKey)) {
      entrance.floors.set(floorKey, {
        floor_number: item.floor_number,
        apartments: [],
        total_devices: 0,
        submitted_devices: 0,
      });
    }
    const floor = entrance.floors.get(floorKey);
    floor.apartments.push(item);
    floor.total_devices += item.total_devices || 0;
    floor.submitted_devices += item.submitted_devices || 0;
    entrance.total_devices += item.total_devices || 0;
    entrance.submitted_devices += item.submitted_devices || 0;
    entrance.total_apartments += 1;
    entrance.status_counts[item.status || 'none'] = (entrance.status_counts[item.status || 'none'] || 0) + 1;
    totalDevices += item.total_devices || 0;
    submittedDevices += item.submitted_devices || 0;
  });

  const corps = Array.from(corpsMap.values())
    .sort((a, b) => compareNatural(a.corp_name, b.corp_name))
    .map((corp) => {
      let corpTotal = 0;
      let corpSubmitted = 0;
      let corpApartments = 0;
      const corpStatusCounts = emptyStatusCounts();
      const entrances = Array.from(corp.entrances.values())
        .sort((a, b) => compareNatural(a.section_number, b.section_number))
        .map((entrance) => {
          const floors = Array.from(entrance.floors.values())
            .sort(compareFloorsDescending)
            .map((floor) => {
              const apartments = [...floor.apartments].sort((a, b) =>
                compareNatural(a.apartment_number, b.apartment_number)
              );
              const floorStatusCounts = countApartmentStatuses(apartments);
              return {
                ...floor,
                apartments,
                status_counts: floorStatusCounts,
                attention_apartments: getAttentionCount(floorStatusCounts),
                total_apartments: apartments.length,
                progress: floor.total_devices > 0 ? floor.submitted_devices / floor.total_devices : 0,
              };
            });
          corpTotal += entrance.total_devices;
          corpSubmitted += entrance.submitted_devices;
          corpApartments += entrance.total_apartments;
          mergeStatusCounts(corpStatusCounts, entrance.status_counts);
          return {
            ...entrance,
            floors,
            attention_apartments: getAttentionCount(entrance.status_counts),
            progress: entrance.total_devices > 0
              ? entrance.submitted_devices / entrance.total_devices
              : 0,
          };
        });
      return {
        ...corp,
        entrances,
        total_apartments: corpApartments,
        total_devices: corpTotal,
        submitted_devices: corpSubmitted,
        status_counts: corpStatusCounts,
        attention_apartments: getAttentionCount(corpStatusCounts),
        progress: corpTotal > 0 ? corpSubmitted / corpTotal : 0,
      };
    });

  return {
    hasFloorData,
    hasEntranceData,
    total_buildings: 1,
    total_apartments: items.length,
    total_devices: totalDevices,
    submitted_devices: submittedDevices,
    status_counts: statusCounts,
    device_counts: deviceCounts,
    attention_apartments: getAttentionCount(statusCounts),
    progress: totalDevices > 0 ? submittedDevices / totalDevices : 0,
    period_start: progress?.period_start,
    period_end: progress?.period_end,
    available_system_types: progress?.available_system_types || [],
    selected_system_types: progress?.selected_system_types || [],
    items,
    buildings: [
      {
        building_id: buildingMeta?.id,
        building_name: buildingMeta?.name,
        building_address: buildingMeta?.address,
        progress: totalDevices > 0 ? submittedDevices / totalDevices : 0,
        corps,
      },
    ],
  };
}

function FloorMnemonic({
  data,
  previousData,
  loading,
  error,
  filter,
  layer,
  month,
  routeMode,
  compareEnabled,
  selectedSystemTypes,
  onFilterChange,
  onLayerChange,
  onMonthChange,
  onRouteModeChange,
  onCompareChange,
  onSystemTypesChange,
  onOpenApartment,
  onCreateTicket,
  canCreateTicket,
}) {
  const tenantDataStartDate = useUnit($tenantDataStartDate);
  const minMonth = tenantDataStartDate ? String(tenantDataStartDate).slice(0, 7) : undefined;
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [apartmentOverview, setApartmentOverview] = useState(null);
  const [apartmentOverviewLoading, setApartmentOverviewLoading] = useState(false);
  const [apartmentOverviewError, setApartmentOverviewError] = useState('');
  const selectedApartmentId = selectedApartment?.apartment?.apartment_id;

  useEffect(() => {
    if (!selectedApartmentId) {
      setApartmentOverview(null);
      setApartmentOverviewError('');
      return undefined;
    }

    let active = true;
    setApartmentOverview(null);
    setApartmentOverviewError('');
    setApartmentOverviewLoading(true);
    apartmentsApi.getOverview(selectedApartmentId)
      .then((overview) => {
        if (active) setApartmentOverview(overview);
      })
      .catch((requestError) => {
        if (!active) return;
        const detail = requestError?.response?.data?.detail;
        setApartmentOverviewError(
          typeof detail === 'string' ? detail : 'Не удалось загрузить данные квартиры.'
        );
      })
      .finally(() => {
        if (active) setApartmentOverviewLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedApartmentId]);
  const visibleBuildings = useMemo(() => (
    (data?.buildings || []).map((building) => {
      const floorSlotsByKey = new Map();
      (building.corps || []).forEach((corp) => {
        (corp.entrances || []).forEach((entrance) => {
          (entrance.floors || []).forEach((floor) => {
            floorSlotsByKey.set(getFloorKey(floor.floor_number), {
              floor_number: floor.floor_number,
            });
          });
        });
      });
      const floorSlots = Array.from(floorSlotsByKey.values()).sort(compareFloorsDescending);
      const corps = (building.corps || [])
        .map((corp) => {
          const entrances = (corp.entrances || [])
            .map((entrance) => {
              const floorByKey = new Map(
                (entrance.floors || []).map((floor) => [getFloorKey(floor.floor_number), floor])
              );
              let hasVisibleApartments = false;
              const floors = floorSlots.map((slot) => {
                const floor = floorByKey.get(getFloorKey(slot.floor_number));
                if (!floor) {
                  return { ...slot, isPlaceholder: true, visibleApartments: [] };
                }
                const visibleApartments = (floor.apartments || []).filter((apartment) =>
                  isApartmentVisible(apartment, filter, layer)
                );
                if (visibleApartments.length) hasVisibleApartments = true;
                return {
                  ...floor,
                  isPlaceholder: visibleApartments.length === 0,
                  visibleApartments,
                };
              });
              return hasVisibleApartments ? { ...entrance, floors } : null;
            })
            .filter(Boolean);
          return entrances.length ? { ...corp, entrances } : null;
        })
        .filter(Boolean);
      return corps.length ? { ...building, corps } : null;
    }).filter(Boolean)
  ), [data, filter, layer]);

  const filters = layer === 'devices'
    ? [
      ['all', `Все ${data?.total_apartments || 0}`],
      ['empty', `Без приборов ${data?.device_counts?.empty || 0}`],
      ['single', `Один прибор ${data?.device_counts?.single || 0}`],
      ['multiple', `Два и более ${data?.device_counts?.multiple || 0}`],
    ]
    : [
      ['all', `Все ${data?.total_apartments || 0}`],
      ['attention', `Требуют внимания ${data?.attention_apartments || 0}`],
      ['complete', `Собраны ${data?.status_counts?.complete || 0}`],
      ['empty', `Без приборов ${data?.status_counts?.no_devices || 0}`],
    ];

  const handleLayerChange = (nextLayer) => {
    onLayerChange(nextLayer);
  };

  const handleOpenFloor = (building, corp, entrance, floor) => {
    setSelectedFloor({ building, corp, entrance, floor });
  };

  const handleOpenApartmentPreview = (apartment, context) => {
    setSelectedApartment({ apartment, ...context });
  };

  const handleCloseApartmentPreview = () => {
    setSelectedApartment(null);
  };

  const handleSystemTypeToggle = (systemType) => {
    setSelectedFloor(null);
    if (!systemType) {
      onSystemTypesChange([]);
      return;
    }
    const next = selectedSystemTypes.includes(systemType)
      ? selectedSystemTypes.filter((value) => value !== systemType)
      : [...selectedSystemTypes, systemType];
    onSystemTypesChange(next);
  };

  const masterRows = useMemo(
    () => buildMasterRows(data, filter, layer),
    [data, filter, layer]
  );

  const previousFloorIndex = useMemo(() => {
    const index = new Map();
    (previousData?.items || []).forEach((item) => {
      const key = `${item.corp_id || ''}|${item.section_number || ''}|${item.floor_number || ''}`;
      const current = index.get(key) || { total: 0, submitted: 0 };
      current.total += Number(item.total_devices || 0);
      current.submitted += Number(item.submitted_devices || 0);
      index.set(key, current);
    });
    return index;
  }, [previousData]);

  const previousTotalDevices = (previousData?.items || []).reduce(
    (sum, item) => sum + Number(item.total_devices || 0),
    0
  );
  const previousSubmittedDevices = (previousData?.items || []).reduce(
    (sum, item) => sum + Number(item.submitted_devices || 0),
    0
  );
  const hasPreviousData = previousTotalDevices > 0;
  const previousProgress = previousTotalDevices
    ? previousSubmittedDevices / previousTotalDevices
    : 0;
  const progressDelta = Math.round((Number(data?.progress || 0) - previousProgress) * 100);
  const selectedPreviousFloor = selectedFloor
    ? previousFloorIndex.get(
        `${selectedFloor.corp.corp_id || ''}|${selectedFloor.entrance.section_number || ''}|${selectedFloor.floor.floor_number || ''}`
      )
    : null;
  const selectedFloorApartments = selectedFloor
    ? ((routeMode
        ? selectedFloor.floor.visibleApartments
        : selectedFloor.floor.apartments) || [])
    : [];

  const exportBaseName = useMemo(() => {
    const building = data?.buildings?.[0];
    const period = String(data?.period_start || '').slice(0, 7) || currentMonth();
    const typeSuffix = selectedSystemTypes.length
      ? `_${selectedSystemTypes.map((value) => safeExportName(value)).join('-')}`
      : '';
    return `Маршрутный лист_${safeExportName(building?.building_address || building?.building_name)}_${period}${typeSuffix}`;
  }, [data, selectedSystemTypes]);

  const handleExcelExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(masterRows);
    worksheet['!cols'] = [
      { wch: 18 }, { wch: 10 }, { wch: 8 }, { wch: 12 }, { wch: 18 },
      { wch: 20 }, { wch: 11 }, { wch: 14 }, { wch: 14 }, { wch: 22 }, { wch: 38 },
      { wch: 24 },
    ];
    worksheet['!autofilter'] = { ref: worksheet['!ref'] || 'A1:K1' };
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Задание мастеру');
    const content = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    downloadBlobAsFile(
      new Blob([content], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `${exportBaseName}.xlsx`
    );
  };

  const handlePdfExport = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.opener = null;
    const building = data?.buildings?.[0];
    const rowsHtml = masterRows.map((row) => `
      <tr>
        <td>${escapeHtml(row['Корпус'])}</td><td>${escapeHtml(row['Подъезд'])}</td>
        <td>${escapeHtml(row['Этаж'])}</td><td>${escapeHtml(row['Квартира'])}</td>
        <td>${escapeHtml(row['Лицевой счёт'])}</td><td>${escapeHtml(row['Типы приборов'])}</td>
        <td>${escapeHtml(row['Приборов'])}</td><td>${escapeHtml(row['С показаниями'])}</td>
        <td>${escapeHtml(row['Без показаний'])}</td><td>${escapeHtml(row['Состояние'])}</td>
        <td>${escapeHtml(row['Проверить приборы'])}</td>
        <td class="mark"></td>
      </tr>`).join('');
    printWindow.document.write(`<!doctype html><html lang="ru"><head><meta charset="utf-8">
      <title>${escapeHtml(exportBaseName)}</title><style>
      @page{size:A4 landscape;margin:10mm}*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#172b45;margin:0}
      h1{font-size:18px;margin:0 0 4px}.meta{font-size:11px;color:#526579;margin-bottom:12px}.meta span{margin-right:18px}
      table{width:100%;border-collapse:collapse;font-size:9px}th{background:#eaf2fb;text-align:left}th,td{border:1px solid #b8c6d5;padding:5px 6px;vertical-align:middle}
      tr{break-inside:avoid}.mark{min-width:74px;height:24px}.footer{margin-top:10px;font-size:9px;color:#64748b}
      </style></head><body><h1>Маршрутный лист по сбору показаний</h1>
      <div class="meta"><span><b>Дом:</b> ${escapeHtml(building?.building_address || building?.building_name)}</span>
      <span><b>Период:</b> ${escapeHtml(formatPeriodDate(data?.period_start))}–${escapeHtml(formatPeriodDate(data?.period_end))}</span>
      <span><b>Строк:</b> ${masterRows.length}</span></div>
      <table><thead><tr><th>Корпус</th><th>Подъезд</th><th>Этаж</th><th>Квартира</th><th>Лицевой счёт</th><th>Тип</th><th>Всего</th><th>Есть</th><th>Нет</th><th>Состояние</th><th>Проверить приборы</th><th>Отметка</th></tr></thead>
      <tbody>${rowsHtml}</tbody></table><div class="footer">Сформировано ${escapeHtml(new Date().toLocaleString('ru-RU'))}</div>
      </body></html>`);
    printWindow.document.close();
    window.setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 250);
  };

  if (loading) {
    return (
      <div className="rc-floor-state">
        <CircularProgress size={22} />
        <span>Загрузка этажей...</span>
      </div>
    );
  }

  if (error) {
    return <div className="rc-floor-state rc-floor-state--error">{error}</div>;
  }

  if (!data?.hasFloorData) {
    return <div className="rc-floor-state">Этажи для квартир пока не заполнены.</div>;
  }

  return (
    <div className={`rc-floor-view ${routeMode ? 'is-route-mode' : ''}`}>
      <div className="rc-floor-topline">
        <div className="rc-floor-summary">
          <span>Квартир: {data.total_apartments}</span>
          <span>Приборы: {data.submitted_devices}/{data.total_devices}</span>
          <span className={data.attention_apartments > 0 ? 'is-warning' : ''}>
            Требуют внимания: {data.attention_apartments}
          </span>
          <strong>{toPercent(data.progress)}%</strong>
          {compareEnabled ? (
            <span className={`rc-floor-trend ${progressDelta < 0 ? 'is-down' : 'is-up'}`}>
              {hasPreviousData
                ? `${progressDelta > 0 ? '+' : ''}${progressDelta} п.п. к ${previousMonth(month)}`
                : `Нет данных за ${previousMonth(month)}`}
            </span>
          ) : null}
        </div>
        <div className={`rc-floor-legend rc-floor-legend--${layer}`} aria-label="Легенда мнемосхемы">
          {layer === 'devices' ? (
            <>
              <span><i className="is-device-empty" /> Нет приборов</span>
              <span><i className="is-device-single" /> Один</span>
              <span><i className="is-device-double" /> Два</span>
              <span><i className="is-device-multiple" /> Три и более</span>
            </>
          ) : (
            <>
              <span><i className="is-complete" /> Собрано</span>
              <span><i className="is-partial" /> Частично</span>
              <span><i className="is-none" /> Нет показаний</span>
              <span><i className="is-empty" /> Нет приборов</span>
            </>
          )}
        </div>
      </div>
      <div className="rc-floor-tools">
        <div className="rc-floor-filter">
          {filters.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`rc-floor-filter__btn ${filter === key ? 'is-active' : ''}`}
              onClick={() => onFilterChange(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="rc-floor-layers" aria-label="Слой мнемосхемы">
          <button
            type="button"
            className={layer === 'readings' ? 'is-active' : ''}
            onClick={() => handleLayerChange('readings')}
          >
            Сбор показаний
          </button>
          <button
            type="button"
            className={layer === 'devices' ? 'is-active' : ''}
            onClick={() => handleLayerChange('devices')}
          >
            Приборы
          </button>
        </div>
      </div>
      <div className="rc-floor-secondary-tools">
        <div className="rc-floor-device-types" aria-label="Типы приборов">
          <span className="rc-floor-device-types__label">Тип приборов</span>
          <button
            type="button"
            className={!selectedSystemTypes.length ? 'is-active' : ''}
            onClick={() => handleSystemTypeToggle(null)}
          >
            Все
          </button>
          {(data.available_system_types || []).map((systemType) => (
            <button
              key={systemType}
              type="button"
              className={selectedSystemTypes.includes(systemType) ? 'is-active' : ''}
              onClick={() => handleSystemTypeToggle(systemType)}
            >
              {systemType}
            </button>
          ))}
        </div>
        <div className="rc-floor-export">
          <span>{masterRows.length} строк</span>
          <button type="button" onClick={handlePdfExport} disabled={!masterRows.length}>
            PDF
          </button>
          <button type="button" onClick={handleExcelExport} disabled={!masterRows.length}>
            Excel
          </button>
        </div>
      </div>
      <div className="rc-floor-work-tools">
        <label className="rc-floor-month">
          <span>Период</span>
          <input
            type="month"
            min={minMonth}
            value={month}
            onChange={(event) => onMonthChange(event.target.value)}
          />
        </label>
        <button
          type="button"
          className={`rc-floor-work-mode ${routeMode ? 'is-active' : ''}`}
          onClick={() => onRouteModeChange(!routeMode)}
        >
          {routeMode ? `Обход: ${masterRows.length} квартир` : 'Режим обхода'}
        </button>
        <button
          type="button"
          className={`rc-floor-compare ${compareEnabled ? 'is-active' : ''}`}
          onClick={() => onCompareChange(!compareEnabled)}
        >
          {compareEnabled ? 'Сравнение включено' : 'Сравнить с прошлым месяцем'}
        </button>
      </div>
      {!visibleBuildings.length ? (
        <div className="rc-floor-empty-filter">По выбранному состоянию квартир нет.</div>
      ) : (
        <div className="rc-floor-buildings">
          {visibleBuildings.map((building) => (
          <section key={building.building_id || 'building'} className="rc-floor-building">
            <div className="rc-floor-building__head">
              <div>
                <div className="rc-floor-building__name">
                  {building.building_name || building.building_address || 'Дом'}
                </div>
                {building.building_address ? (
                  <div className="rc-floor-building__address">{building.building_address}</div>
                ) : null}
              </div>
              <span>{toPercent(building.progress)}%</span>
            </div>
            <div className="rc-floor-corps">
              {building.corps.map((corp) => (
                <div key={corp.corp_id || corp.corp_name || 'corp'} className="rc-floor-corp">
                  <div className="rc-floor-corp__title">
                    <span>{corp.corp_name || 'Корпус'}</span>
                    <span>
                      {corp.total_apartments} кв. · {corp.submitted_devices}/{corp.total_devices}
                    </span>
                  </div>
                  <div className="rc-floor-entrances">
                    {corp.entrances.map((entrance) => (
                        <div
                          key={entrance.section_number || 'none'}
                          className={`rc-floor-entrance rc-floor-entrance--${
                            layer === 'devices'
                              ? 'device-map'
                              : floorTone(entrance.progress, entrance.total_devices)
                          }`}
                        >
                          <div className="rc-floor-entrance__head">
                            <div className="rc-floor-entrance__title">
                              <span>
                                {entrance.section_number ? `Подъезд ${entrance.section_number}` : 'Подъезд не указан'}
                              </span>
                              <span>{toPercent(entrance.progress)}%</span>
                            </div>
                            {entrance.attention_apartments > 0 ? (
                              <div className="rc-floor-entrance__alert">
                                {entrance.attention_apartments} кв. требуют внимания
                              </div>
                            ) : null}
                          </div>
                          <div className="rc-floor-stack">
                            {entrance.floors.map((floor) => {
                              if (floor.isPlaceholder) {
                                return (
                                  <div
                                    key={getFloorKey(floor.floor_number)}
                                    className="rc-floor-row rc-floor-row--placeholder"
                                    aria-hidden="true"
                                  />
                                );
                              }
                              const percent = toPercent(floor.progress);
                              const tone = layer === 'devices'
                                ? getFloorDeviceTone(floor.total_devices, floor.total_apartments)
                                : floorTone(floor.progress, floor.total_devices);
                              const barWidth = layer === 'devices'
                                ? Math.min(
                                  100,
                                  Math.round(
                                    (floor.total_devices / Math.max(floor.total_apartments * 3, 1)) * 100
                                  )
                                )
                                : percent;
                              const previousFloor = previousFloorIndex.get(
                                `${corp.corp_id || ''}|${entrance.section_number || ''}|${floor.floor_number || ''}`
                              );
                              const previousFloorProgress = previousFloor?.total
                                ? previousFloor.submitted / previousFloor.total
                                : 0;
                              const floorDelta = Math.round((floor.progress - previousFloorProgress) * 100);
                              return (
                                <div
                                  key={getFloorKey(floor.floor_number)}
                                  className={`rc-floor-row rc-floor-row--${tone}`}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => handleOpenFloor(building, corp, entrance, floor)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      handleOpenFloor(building, corp, entrance, floor);
                                    }
                                  }}
                                  title="Открыть информацию по этажу"
                                >
                                  <div className="rc-floor-row__label">{floor.floor_number ?? '—'}</div>
                                  <div className="rc-floor-row__bar">
                                    <span style={{ width: `${barWidth}%` }} />
                                  </div>
                                  <div className="rc-floor-row__meta">
                                    <strong>{layer === 'devices' ? floor.total_devices : `${percent}%`}</strong>
                                    <span>
                                      {compareEnabled && layer === 'readings'
                                        ? previousFloor?.total
                                          ? `${floorDelta > 0 ? '+' : ''}${floorDelta} п.п.`
                                          : '—'
                                        : `${floor.total_apartments} кв.`}
                                    </span>
                                  </div>
                                  <div className="rc-floor-apartments">
                                    {floor.visibleApartments.slice(0, 4).map((apartment) => (
                                      <button
                                        key={apartment.apartment_id}
                                        type="button"
                                        className={`rc-floor-apt rc-floor-apt--${
                                          layer === 'devices'
                                            ? getDeviceTone(apartment.total_devices)
                                            : apartment.status
                                        }`}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleOpenApartmentPreview(apartment, {
                                            building,
                                            corp,
                                            entrance,
                                            floor,
                                          });
                                        }}
                                        onKeyDown={(event) => event.stopPropagation()}
                                        title={`Квартира ${apartment.apartment_number}: ${apartment.submitted_devices}/${apartment.total_devices}`}
                                      >
                                        {apartment.apartment_number}
                                      </button>
                                    ))}
                                    {floor.visibleApartments.length > 4 ? (
                                      <span
                                        className="rc-floor-apt-more"
                                        title={`Ещё квартир: ${floor.visibleApartments.length - 4}`}
                                      >
                                        +{floor.visibleApartments.length - 4}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="rc-floor-entrance__foot">
                            <span>{entrance.total_apartments} кв.</span>
                            <span>{entrance.submitted_devices}/{entrance.total_devices}</span>
                          </div>
                        </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
          ))}
        </div>
      )}
      <Drawer
        anchor="right"
        open={Boolean(selectedFloor)}
        onClose={() => setSelectedFloor(null)}
        PaperProps={{ className: 'rc-floor-drawer' }}
      >
        {selectedFloor ? (
          <div className="rc-floor-drawer__inner">
            <div className="rc-floor-drawer__head">
              <div>
                <div className="rc-floor-drawer__eyebrow">
                  {selectedFloor.corp.corp_name || 'Корпус'} · {
                    selectedFloor.entrance.section_number
                      ? `Подъезд ${selectedFloor.entrance.section_number}`
                      : 'Подъезд не указан'
                  }
                </div>
                <h2>Этаж {selectedFloor.floor.floor_number ?? 'не указан'}</h2>
                <p>{selectedFloor.building.building_address || selectedFloor.building.building_name}</p>
              </div>
              <button
                type="button"
                className="rc-floor-drawer__close"
                onClick={() => setSelectedFloor(null)}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            <div className="rc-floor-drawer__progress">
              <div>
                <span>Сбор показаний</span>
                <strong>{toPercent(selectedFloor.floor.progress)}%</strong>
              </div>
              <div className="rc-floor-drawer__bar">
                <span style={{ width: `${toPercent(selectedFloor.floor.progress)}%` }} />
              </div>
              {compareEnabled ? (
                <div className="rc-floor-drawer__comparison">
                  Прошлый месяц: {selectedPreviousFloor?.total
                    ? `${Math.round((selectedPreviousFloor.submitted / selectedPreviousFloor.total) * 100)}%`
                    : 'нет данных'}
                </div>
              ) : null}
            </div>

            <div className="rc-floor-drawer__stats">
              <div><strong>{selectedFloorApartments.length}</strong><span>квартир</span></div>
              <div><strong>{selectedFloor.floor.total_devices}</strong><span>приборов</span></div>
              <div>
                <strong>{selectedFloor.floor.submitted_devices}</strong>
                <span>с показаниями</span>
              </div>
            </div>

            <div className="rc-floor-drawer__section-head">
              <strong>Квартиры</strong>
              <span>{selectedFloor.floor.attention_apartments || 0} требуют внимания</span>
            </div>
            <div className="rc-floor-drawer__apartments">
              {selectedFloorApartments.map((apartment) => (
                <button
                  key={apartment.apartment_id}
                  type="button"
                  className="rc-floor-drawer__apartment"
                  onClick={() => {
                    handleOpenApartmentPreview(apartment, selectedFloor);
                    setSelectedFloor(null);
                  }}
                >
                  <span className={`rc-floor-drawer__status rc-floor-drawer__status--${apartment.status}`} />
                  <span className="rc-floor-drawer__apartment-number">
                    Квартира {apartment.apartment_number}
                  </span>
                  <span className="rc-floor-drawer__apartment-state">
                    {apartmentStatusLabel(apartment.status)}
                    {apartment.missing_device_labels?.length ? (
                      <small title={apartment.missing_device_labels.join('; ')}>
                        {apartment.missing_device_labels.join('; ')}
                      </small>
                    ) : null}
                  </span>
                  <strong>{apartment.submitted_devices}/{apartment.total_devices}</strong>
                  <span className="rc-floor-drawer__arrow">›</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </Drawer>
      <Drawer
        anchor="bottom"
        open={Boolean(selectedApartment)}
        onClose={handleCloseApartmentPreview}
        PaperProps={{ className: 'rc-apartment-sheet' }}
        ModalProps={{ keepMounted: true }}
      >
        {selectedApartment ? (
          <div className="rc-apartment-sheet__inner">
            <div className="rc-apartment-sheet__handle" aria-hidden="true" />
            <div className="rc-apartment-sheet__head">
              <div>
                <div className="rc-apartment-sheet__eyebrow">
                  {[
                    selectedApartment.corp?.corp_name,
                    selectedApartment.entrance?.section_number
                      ? `Подъезд ${selectedApartment.entrance.section_number}`
                      : null,
                    selectedApartment.floor?.floor_number != null
                      ? `Этаж ${selectedApartment.floor.floor_number}`
                      : null,
                  ].filter(Boolean).join(' · ')}
                </div>
                <h2>Квартира {apartmentOverview?.display_number || selectedApartment.apartment.apartment_number}</h2>
                <p>
                  {selectedApartment.building?.building_address
                    || selectedApartment.building?.building_name
                    || apartmentOverview?.building_name}
                </p>
              </div>
              <button
                type="button"
                className="rc-apartment-sheet__close"
                onClick={handleCloseApartmentPreview}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            <div className="rc-apartment-sheet__content">
              <div className="rc-apartment-sheet__summary">
                <div>
                  <span>Сбор показаний</span>
                  <strong className={`is-${selectedApartment.apartment.status || 'none'}`}>
                    {apartmentStatusLabel(selectedApartment.apartment.status)}
                  </strong>
                </div>
                <div>
                  <span>Приборы</span>
                  <strong>
                    {selectedApartment.apartment.submitted_devices}/{selectedApartment.apartment.total_devices}
                  </strong>
                </div>
                <div>
                  <span>Лицевой счёт</span>
                  <strong>{apartmentOverview?.apartment?.account_number || selectedApartment.apartment.account_number || 'Не указан'}</strong>
                </div>
                <div>
                  <span>Площадь</span>
                  <strong>
                    {apartmentOverview?.apartment?.area
                      ? `${apartmentOverview.apartment.area} м²`
                      : 'Не указана'}
                  </strong>
                </div>
              </div>

              {apartmentOverviewError ? (
                <div className="rc-apartment-sheet__error">{apartmentOverviewError}</div>
              ) : null}

              <div className="rc-apartment-sheet__section-head">
                <strong>Приборы</strong>
                {apartmentOverviewLoading ? <CircularProgress size={18} /> : (
                  <span>{apartmentOverview?.devices?.length ?? selectedApartment.apartment.total_devices} шт.</span>
                )}
              </div>

              {!apartmentOverviewLoading && apartmentOverview?.devices?.length ? (
                <div className="rc-apartment-sheet__devices">
                  {apartmentOverview.devices.map((device) => {
                    const unit = getReadingUnit(device.system_type);
                    return (
                      <div key={device.id} className="rc-apartment-sheet__device">
                        <div className="rc-apartment-sheet__device-head">
                          <div>
                            <strong>{device.system_type || device.name || 'Прибор'}</strong>
                            <span>№ {device.serial_number || 'не указан'}</span>
                          </div>
                          <span className={device.latest_reading_ts ? 'is-ready' : 'is-missing'}>
                            {device.latest_reading_ts ? 'Есть показания' : 'Нет показаний'}
                          </span>
                        </div>
                        <div className="rc-apartment-sheet__reading">
                          <strong>{formatReadingValue(device.latest_reading_value)}</strong>
                          {device.latest_reading_value != null && unit ? <span>{unit}</span> : null}
                        </div>
                        <div className="rc-apartment-sheet__device-meta">
                          <span>{formatApartmentDate(device.latest_reading_ts, true)}</span>
                          {device.verification_date ? (
                            <span>Поверка до {formatApartmentDate(device.verification_date)}</span>
                          ) : null}
                          {device.manufacturer ? <span>{device.manufacturer}</span> : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {!apartmentOverviewLoading && apartmentOverview && !apartmentOverview.devices.length ? (
                <div className="rc-apartment-sheet__empty">Приборы не добавлены.</div>
              ) : null}

              {selectedApartment.apartment.missing_device_labels?.length ? (
                <div className="rc-apartment-sheet__notice">
                  <strong>Нет показаний</strong>
                  <span>{selectedApartment.apartment.missing_device_labels.join(', ')}</span>
                </div>
              ) : null}
            </div>

            <div className="rc-apartment-sheet__actions">
              {apartmentOverview?.counters?.tickets_open > 0 ? (
                <span>Открытых заявок: {apartmentOverview.counters.tickets_open}</span>
              ) : <span />}
              <div className="rc-apartment-sheet__action-buttons">
                {canCreateTicket ? (
                  <button
                    type="button"
                    className="is-secondary"
                    onClick={() => onCreateTicket(selectedApartment.apartment.apartment_id)}
                  >
                    Создать заявку
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => onOpenApartment(selectedApartment.apartment.apartment_id)}
                >
                  Открыть квартиру
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}

export default function BuildingCorpsPage() {
  const { buildingId } = useParams();                 // из маршрута /building/:buildingId/corps
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [corpsMap, pending, canManage, complexes, isSettlementTenant] = useUnit([
    $corpsByBuilding,
    fetchCorpsByBuildingFx.pending,
    $canDeleteComplex,
    $complexes,
    $isSettlementTenant,
  ]);

  const items = useMemo(
    () => corpsMap[String(buildingId)] || [],
    [corpsMap, buildingId]
  );
  const namedCorps = useMemo(
    () => items.filter((item) => !item.is_default),
    [items]
  );
  const defaultCorps = useMemo(
    () => items.find((item) => item.is_default) || null,
    [items]
  );
  const isFlatBuilding = namedCorps.length === 0;
  const corpsLoaded = Object.prototype.hasOwnProperty.call(corpsMap, String(buildingId));
  const visibleCorps = useMemo(() => {
    if (isFlatBuilding) return [];
    const result = [...namedCorps];
    if (defaultCorps && Number(defaultCorps.device_count || defaultCorps.device_counts?.total || 0) > 0) {
      result.unshift({ ...defaultCorps, name: 'Без корпуса' });
    }
    return result;
  }, [isFlatBuilding, namedCorps, defaultCorps]);
  const [openingDefault, setOpeningDefault] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '' });

  // Для дома без корпусов заранее создаём служебный корпус — квартиры и приборы живут в нём.
  useEffect(() => {
    if (!buildingId || !corpsLoaded || pending || !isFlatBuilding || defaultCorps || openingDefault) return;
    let cancelled = false;
    setOpeningDefault(true);
    buildingCorpsApi
      .ensureDefault(String(buildingId))
      .then(async () => {
        if (cancelled) return;
        try { await fetchCorpsByBuildingFx(String(buildingId)); } catch {}
      })
      .catch((err) => {
        if (cancelled) return;
        setSnack({
          open: true,
          msg: err?.response?.data?.detail || 'Не удалось подготовить дом.',
        });
      })
      .finally(() => {
        if (!cancelled) setOpeningDefault(false);
      });
    return () => { cancelled = true; };
  }, [buildingId, corpsLoaded, pending, isFlatBuilding, defaultCorps, openingDefault]);

  const [exporting, setExporting] = useState(false);
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const { meta: buildingMeta, reload: reloadBuildingMeta } = useBuildingMeta(buildingId);
  const settlementMode = useMemo(() => {
    if (isSettlementTenant) return true;
    const complexId = buildingMeta?.residential_complex_id;
    if (!complexId) return false;
    const complex = (complexes || []).find((item) => String(item.id) === String(complexId));
    return isSettlementComplex(complex);
  }, [isSettlementTenant, buildingMeta?.residential_complex_id, complexes]);
  const [heatEnableLoading, setHeatEnableLoading] = useState(false);
  const [heatEnableError, setHeatEnableError] = useState('');
  const [notifyActiveCount, setNotifyActiveCount] = useState(0);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [floorProgress, setFloorProgress] = useState(null);
  const [floorPreviousProgress, setFloorPreviousProgress] = useState(null);
  const [floorLoading, setFloorLoading] = useState(false);
  const [floorError, setFloorError] = useState('');
  const viewParam = searchParams.get('view');
  const rawViewMode = viewParam === 'floors'
    ? 'floors'
    : viewParam === 'channels'
      ? 'channels'
      : viewParam === 'orphans'
        ? 'orphans'
        : 'corps';
  const viewMode = settlementMode && rawViewMode === 'floors' ? 'corps' : rawViewMode;
  const [orphanCount, setOrphanCount] = useState(0);

  const reloadOrphanCount = useCallback(async () => {
    if (!buildingId) {
      setOrphanCount(0);
      return;
    }
    try {
      const data = await buildingsApi.getOrphanDevices(String(buildingId));
      setOrphanCount(Number(data?.total || 0));
    } catch {
      setOrphanCount(0);
    }
  }, [buildingId]);

  useEffect(() => {
    reloadOrphanCount();
  }, [reloadOrphanCount]);
  const floorLayer = searchParams.get('layer') === 'devices' ? 'devices' : 'readings';
  const allowedFloorFilters = floorLayer === 'devices'
    ? ['all', 'empty', 'single', 'multiple']
    : ['all', 'attention', 'complete', 'empty'];
  const requestedFloorFilter = searchParams.get('status') || 'all';
  const floorFilter = allowedFloorFilters.includes(requestedFloorFilter)
    ? requestedFloorFilter
    : 'all';
  const floorMonth = /^\d{4}-\d{2}$/.test(searchParams.get('month') || '')
    ? searchParams.get('month')
    : currentMonth();
  const floorRouteMode = searchParams.get('route') === '1';
  const floorCompareEnabled = searchParams.get('compare') === '1';
  const floorTypesParam = searchParams.get('types') || '';
  const floorSystemTypes = useMemo(
    () => floorTypesParam.split(',').map((value) => value.trim()).filter(Boolean),
    [floorTypesParam]
  );

  const updateMnemonicParams = useCallback((patch) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      Object.entries(patch).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    if (settlementMode && viewParam === 'floors') {
      updateMnemonicParams({ view: null });
    }
  }, [settlementMode, viewParam, updateMnemonicParams]);

  useEffect(() => {
    if (buildingId) {
      fetchCorpsByBuildingFx(String(buildingId));
    }
  }, [buildingId]); // eslint-disable-line

  const loadNotificationStatus = useCallback(async () => {
    if (!buildingId) return;
    setNotifyLoading(true);
    try {
      const data = await notificationSchedulesApi.list(String(buildingId));
      const active = (data || []).filter((item) => item.enabled).length;
      setNotifyActiveCount(active);
    } catch (err) {
      setNotifyActiveCount(0);
    } finally {
      setNotifyLoading(false);
    }
  }, [buildingId]);

  useEffect(() => {
    loadNotificationStatus();
  }, [loadNotificationStatus]);

  const handleEnableHeatMode = useCallback(async () => {
    if (!buildingId) return;
    setHeatEnableLoading(true);
    setHeatEnableError('');
    try {
      await buildingsApi.updateBuilding(String(buildingId), {
        heat_allocator_enabled: true,
      });
      reloadBuildingMeta();
    } catch (err) {
      setHeatEnableError('Не удалось включить режим УРТ.');
    } finally {
      setHeatEnableLoading(false);
    }
  }, [buildingId, reloadBuildingMeta]);

  const handleExport = useCallback(async () => {
    const complexId = buildingMeta?.residential_complex_id;
    if (!buildingId || !complexId) {
      console.warn('Нет данных для выгрузки отчёта по дому');
      return;
    }
    try {
      setExporting(true);
      const { data, fileName } = await reportsApi.downloadResidentialConsumptionReport({
        residentialComplexId: String(complexId),
        buildingId: String(buildingId),
      });
      const fallbackBase = buildFileName(
        ['consumption', String(complexId), `building-${buildingId}`],
        'consumption-report'
      );
      const finalName = typeof fileName === 'string' && fileName.trim().length
        ? fileName.trim()
        : `${fallbackBase}.xlsx`;
      downloadBlobAsFile(data, finalName);
    } catch (error) {
      console.error('Не удалось выгрузить отчёт по дому', error);
    } finally {
      setExporting(false);
    }
  }, [buildingMeta, buildingId]);

  const handleOpenNotifyDialog = () => {
    setNotifyDialogOpen(true);
  };

  const handleCloseNotifyDialog = () => {
    setNotifyDialogOpen(false);
    loadNotificationStatus();
  };

  const hasActiveNotifications = notifyActiveCount > 0;
  const floorMnemonicData = useMemo(
    () => buildFloorMnemonic(floorProgress, buildingMeta),
    [buildingMeta, floorProgress]
  );

  useEffect(() => {
    if (viewMode !== 'floors' || !buildingId) return;
    let cancelled = false;
    setFloorLoading(true);
    setFloorError('');
    const params = {
      month: floorMonth,
      source: 'ANY',
      system_type: floorSystemTypes.length ? floorSystemTypes : undefined,
    };
    Promise.all([
      manualApi.getSubmissionProgress(String(buildingId), params),
      floorCompareEnabled
        ? manualApi.getSubmissionProgress(String(buildingId), {
            ...params,
            month: previousMonth(floorMonth),
            include_details: false,
          })
        : Promise.resolve(null),
    ])
      .then(([data, previousData]) => {
        if (!cancelled) {
          setFloorProgress(data);
          setFloorPreviousProgress(previousData);
        }
      })
      .catch((err) => {
        console.error('load floor progress failed', err);
        if (!cancelled) setFloorError('Не удалось загрузить мнемосхему.');
      })
      .finally(() => {
        if (!cancelled) setFloorLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [buildingId, viewMode, floorMonth, floorCompareEnabled, floorSystemTypes]);

  return (
    <div
      className="home-container"
      style={{
        width: '100%',
        maxWidth: 'calc(100vw - var(--sidebar-current-width) - 40px)',
        margin: '0 auto',
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 24,
        overflowX: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <button className="rc-btn" onClick={() => navigate(-1)}>← Назад</button>

        {settlementMode && buildingMeta ? (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-color)' }}>
              {buildingMeta.name || 'Дом'}
            </div>
            {buildingMeta.address ? (
              <div
                style={{
                  maxWidth: 560,
                  overflow: 'hidden',
                  color: 'var(--muted-color)',
                  fontSize: 12,
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={buildingMeta.address}
              >
                {buildingMeta.address}
              </div>
            ) : null}
          </div>
        ) : null}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {canManage && buildingMeta && !buildingMeta.heat_allocator_enabled && (
            <button
              className="rc-btn"
              onClick={handleEnableHeatMode}
              disabled={heatEnableLoading}
            >
              {heatEnableLoading ? 'Включаем УРТ…' : 'Включить УРТ'}
            </button>
          )}
          {canManage && !settlementMode && (
            <button className="rc-btn" onClick={() => setOpenAdd(true)}>
              {isFlatBuilding ? 'Добавить корпуса' : 'Добавить корпус'}
            </button>
          )}
          {canManage && (
            <button
              className="rc-btn"
              onClick={() => navigate(`/building/${buildingId}/manual-progress`)}
            >
              Прогресс показаний
            </button>
          )}
          {canManage && (
            <Tooltip
              title={
                hasActiveNotifications
                  ? `Активные расписания: ${notifyActiveCount}`
                  : 'Нет активных расписаний'
              }
            >
              <span>
                <Badge
                  variant="dot"
                  color="success"
                  overlap="circular"
                  invisible={!hasActiveNotifications || notifyLoading}
                  sx={{
                    '& .MuiBadge-badge': {
                      width: 10,
                      height: 10,
                      minWidth: 10,
                      borderRadius: '50%',
                      boxShadow: '0 0 0 2px #fff, 0 0 6px rgba(46, 125, 50, 0.65)',
                    },
                  }}
                >
                  <button className="rc-btn" onClick={handleOpenNotifyDialog}>
                    {settlementMode ? 'Оповещения владельцам' : 'Оповещения жильцам'}
                  </button>
                </Badge>
              </span>
            </Tooltip>
          )}
        </div>
      </div>
      {heatEnableError && (
        <div style={{ color: '#d32f2f', marginBottom: 12 }}>{heatEnableError}</div>
      )}

      {canManage && (
        <>
          <ManualSubmissionPanel buildingId={buildingId} />
          
          <NotificationSchedulesDialog
            open={notifyDialogOpen}
            onClose={handleCloseNotifyDialog}
            buildingId={buildingId}
            buildingMeta={buildingMeta}
          />
{buildingMeta?.heat_allocator_enabled ? (
            <HeatManagementPanel
              buildingId={buildingId}
              onModeChange={reloadBuildingMeta}
            />
          ) : null}
        </>
      )}

      <ComplexesToolbar
        viewTab={false}
        activeTab={
          viewMode === 'floors'
            ? 'Мнемосхема'
            : viewMode === 'channels'
              ? 'Каналы'
              : viewMode === 'orphans'
                ? 'Непривязанные'
                : (settlementMode || isFlatBuilding ? 'Приборы' : 'Список корпусов')
        }
        onTabChange={() => {}}
        onExport={handleExport}
        exportLoading={exporting}
        exportDisabled={!buildingId || !buildingMeta?.residential_complex_id || viewMode === 'channels' || viewMode === 'orphans'}
        onSearch={() => {}}
        onFilter={() => {}}
        actionsPrefix={(
          <div className="rc-corps-mode-switch" aria-label="Переключение вида дома">
            <button
              type="button"
              className={`rc-corps-mode-switch__btn ${viewMode === 'corps' ? 'is-active' : ''}`}
              onClick={() => updateMnemonicParams({ view: null })}
            >
              {settlementMode || isFlatBuilding ? 'Приборы' : 'Корпуса'}
            </button>
            {!settlementMode && (
              <button
                type="button"
                className={`rc-corps-mode-switch__btn ${viewMode === 'floors' ? 'is-active' : ''}`}
                onClick={() => updateMnemonicParams({ view: 'floors' })}
              >
                Мнемосхема
              </button>
            )}
            <button
              type="button"
              className={`rc-corps-mode-switch__btn ${viewMode === 'channels' ? 'is-active' : ''}`}
              onClick={() => updateMnemonicParams({ view: 'channels' })}
            >
              Каналы
            </button>
            <button
              type="button"
              className={`rc-corps-mode-switch__btn ${viewMode === 'orphans' ? 'is-active' : ''}`}
              onClick={() => updateMnemonicParams({ view: 'orphans' })}
            >
              <Badge
                color="warning"
                badgeContent={orphanCount}
                invisible={!orphanCount}
                sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}
              >
                Непривязанные
              </Badge>
            </button>
          </div>
        )}
      />

      <div className="rc-fluid">
        {viewMode === 'orphans' ? (
          <OrphanDevicesPanel
            buildingId={buildingId}
            onChanged={() => {
              reloadOrphanCount();
              try { fetchCorpsByBuildingFx(String(buildingId)); } catch {}
            }}
          />
        ) : viewMode === 'channels' ? (
          <BuildingChannelMap buildingId={buildingId} />
        ) : viewMode === 'floors' ? (
          <div className="rc-floor-panel">
            <FloorMnemonic
              data={floorMnemonicData}
              previousData={floorPreviousProgress}
              loading={floorLoading}
              error={floorError}
              filter={floorFilter}
              layer={floorLayer}
              month={floorMonth}
              routeMode={floorRouteMode}
              compareEnabled={floorCompareEnabled}
              selectedSystemTypes={floorSystemTypes}
              onFilterChange={(value) => updateMnemonicParams({
                status: value === 'all' ? null : value,
                route: floorRouteMode && value !== 'attention' ? null : floorRouteMode ? '1' : null,
              })}
              onLayerChange={(value) => updateMnemonicParams({
                layer: value === 'readings' ? null : value,
                status: null,
                route: null,
              })}
              onMonthChange={(value) => updateMnemonicParams({ month: value === currentMonth() ? null : value })}
              onRouteModeChange={(enabled) => updateMnemonicParams({
                route: enabled ? '1' : null,
                layer: enabled ? null : floorLayer === 'readings' ? null : floorLayer,
                status: enabled ? 'attention' : floorFilter === 'attention' ? null : floorFilter,
              })}
              onCompareChange={(enabled) => updateMnemonicParams({ compare: enabled ? '1' : null })}
              onSystemTypesChange={(values) => updateMnemonicParams({
                types: values.length ? values.join(',') : null,
              })}
              onOpenApartment={(apartmentId) => navigate(`/apartments/${apartmentId}`, {
                state: { returnTo: `${location.pathname}${location.search}` },
              })}
              onCreateTicket={(apartmentId) => navigate(
                `/tickets?create=1&apartment_id=${encodeURIComponent(apartmentId)}`,
                { state: { returnTo: `${location.pathname}${location.search}` } }
              )}
              canCreateTicket={canManage}
            />
          </div>
        ) : isFlatBuilding ? (
          openingDefault || !defaultCorps ? (
            <div className="rc-empty" style={{ padding: 40, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </div>
          ) : (
            <BuildingDevicesSection
              buildingId={String(buildingId)}
              corpsId={String(defaultCorps.id)}
              houseMode={settlementMode}
            />
          )
        ) : (
          <CorpsList
          items={visibleCorps.map((c) => ({
            id: c.id,
            name: c.name,
            created_at: c.created_at,
            buildingId: c.building_id ?? buildingId,
            devices: {
              cold: c.device_counts?.hvs ?? 0,
              hot: c.device_counts?.gvs ?? 0,
              heat: c.device_counts?.heating ?? 0,
              heatAllocators: c.device_counts?.heat_allocators ?? 0,
              electricity: c.device_counts?.electricity ?? 0,
              total: c.device_counts?.total ?? c.device_count ?? 0,
            },
          }))}
          loading={pending}
          emptyActionLabel={canManage ? 'Добавить корпус' : undefined}
          onEmptyAction={canManage ? () => setOpenAdd(true) : undefined}
          onItemClick={(corpsId) => {
            navigate(`/corps/${corpsId}?buildingId=${buildingId}`);
          }}
          onEdited={async () => {
            try { await fetchCorpsByBuildingFx(String(buildingId)); } catch {}
          }}
        />
        )}
      </div>

      {/* Диалог создания корпуса (только для admin/superadmin) */}
      {canManage && (
        <AddCorpsDialog
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          buildingId={String(buildingId)}
          onSuccess={async () => {
            showSoftSuccess('Корпус добавлен');
            try { await fetchCorpsByBuildingFx(String(buildingId)); } catch {}
          }}
        />
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ open: false, msg: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack({ open: false, msg: '' })}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </div>
  );
}
